import * as React from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { prepayHotelRatesRemovalEnabled } from '@src/store/selectors';
// Actions, Models & Interfaces
import { HotelFareModel, OptionModel } from '@src/shared/src/models';
// Components
import { CheckboxMultiSelect } from '@toolkit/ui';
import { CustomSelect } from '@toolkit/ui/CustomSelect';

// Styles
import '../styles/HotelFareFilter.scss';
// Store
type Props<K extends keyof HotelFareModel = keyof HotelFareModel> = {
  selected: Record<K, string[]>;
  options: Record<K, OptionModel[]>;
  disabled?: K[];
  onChange: (filters: Record<K, string[]>) => void;
};
type HotelFareFilterProps = Props<'breakfast' | 'roomType' | 'refundableUntil' | 'paymentType'>;

export type FareFilterType = HotelFareFilterProps['selected'];

export const HotelFareFilter: React.FC<HotelFareFilterProps> = (props) => {
  const { selected, onChange, options, disabled } = props;
  const { t } = useTranslation();
  const isPrepayHotelRatesRemovalEnabled = useSelector(prepayHotelRatesRemovalEnabled);

  const getLabel = (filtername: string) => {
    const opts = options[filtername];
    const selectedOpts = selected[filtername];

    switch (selectedOpts.length) {
      case 0:
        return t('global.none');
      case opts.length:
        return t('global.all');
      case 1:
        return opts.find((option) => option.value === selectedOpts[0])?.label;
      default:
        return `${selectedOpts.length} ${t(`hotelFare.filter.${filtername}`)}`;
    }
  };

  const ALL_OPTION: OptionModel = {
    label: t('global.all'),
    value: 'all',
  };

  const toggleFilter = (filterName: keyof FareFilterType, values: string[]) => {
    onChange({
      ...selected,
      [filterName]: values,
    });
  };

  const onSelectChange = (option: OptionModel, filterName: keyof FareFilterType) =>
    toggleFilter(
      filterName,
      option.value === ALL_OPTION.value
        ? options[filterName].map((option) => option.value)
        : [option.value],
    );

  const getSelectedValues = (filterName: keyof FareFilterType) => {
    if (selected[filterName].length > 1) {
      return ALL_OPTION.value;
    }
    return options[filterName].find((option) => option.value === selected[filterName][0])?.value;
  };

  return (
    <div className="hotel-fare-filter">
      <div className="hotel-fare-filter-item">
        <div className="hotel-fare-filter-label">{t('hotelFare.filter.roomType')}</div>
        <CheckboxMultiSelect
          label={getLabel('roomType')}
          name="roomType"
          disabled={disabled.includes('roomType')}
          options={options.roomType}
          onChange={(values: string[]) => toggleFilter('roomType', values)}
          selectedOptions={selected['roomType']}
        />
      </div>

      <div className="hotel-fare-filter-item">
        <div className="hotel-fare-filter-label">{t('hotelFare.filter.breakfast')}</div>
        <CustomSelect
          options={[ALL_OPTION].concat(options.breakfast)}
          isDisabled={disabled.includes('breakfast')}
          selectedValue={getSelectedValues('breakfast')}
          onChange={(option: OptionModel) => onSelectChange(option, 'breakfast')}
        />
      </div>
      <div className="hotel-fare-filter-item">
        <div className="hotel-fare-filter-label">{t('hotelFare.filter.condition')}</div>
        <CheckboxMultiSelect
          label={getLabel('refundableUntil')}
          name="refundableUntil"
          disabled={disabled.includes('refundableUntil')}
          options={options.refundableUntil}
          onChange={(values: string[]) => toggleFilter('refundableUntil', values)}
          selectedOptions={selected['refundableUntil']}
        />
      </div>

      <div className="hotel-fare-filter-item">
        <div className="hotel-fare-filter-label">{t('hotelFare.filter.prePaidRate')}</div>
        <CustomSelect
          options={[ALL_OPTION].concat(options.paymentType)}
          isDisabled={disabled.includes('paymentType') || isPrepayHotelRatesRemovalEnabled}
          selectedValue={isPrepayHotelRatesRemovalEnabled ? '' : getSelectedValues('paymentType')}
          onChange={(option: OptionModel) => onSelectChange(option, 'paymentType')}
        />
      </div>
    </div>
  );
};
